import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Divider, CircularProgress, IconButton, Collapse, Button, TextField, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Stack, Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotReceivedRecipients } from '../slices/recipientSlice';
import { bankLabel, branchLabel } from '../utils/bankLabel';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { exportToExcel, exportToPrintWindow } from '../utils/exportUtils';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { updateRecipient, deleteRecipient } from '../slices/recipientSlice';
import { useToast } from '../context/ToastContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { formatPhoneForWhatsApp } from '../utils/phoneUtils';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const MotionPaper = motion(Paper);

const ChequesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { notReceived: recipients, status } = useSelector((state) => state.recipients);
  const [tableRows, setTableRows] = useState([]);
  const [allDates, setAllDates] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Filter states - initialized from localStorage
  const [nameFilter, setNameFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('cheques_page_filters');
      return saved ? JSON.parse(saved).nameFilter || '' : '';
    } catch { return ''; }
  });
  const [dateFromFilter, setDateFromFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('cheques_page_filters');
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed?.dateFrom ? dayjs(parsed.dateFrom) : null;
    } catch { return null; }
  });
  const [dateToFilter, setDateToFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('cheques_page_filters');
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed?.dateTo ? dayjs(parsed.dateTo) : null;
    } catch { return null; }
  });
  const [filteredRecipients, setFilteredRecipients] = useState([]);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    recipientId: null,
    studentName: ''
  });

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    recipientId: null,
    studentName: ''
  });

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('cheques_page_filters', JSON.stringify({
      nameFilter,
      dateFrom: dateFromFilter ? dateFromFilter.toISOString() : null,
      dateTo: dateToFilter ? dateToFilter.toISOString() : null,
    }));
  }, [nameFilter, dateFromFilter, dateToFilter]);


  useEffect(() => {
    dispatch(fetchNotReceivedRecipients());
  }, [dispatch]);

  // Helper function to get the display date (cheque date if exists, otherwise payment date)
  const getDisplayDate = (recipient) => {
    if (recipient.cheque && recipient.cheque.cheque_date) {
      return recipient.cheque.cheque_date;
    }
    return recipient.date;
  };

  // Helper function to get date type label
  const getDateTypeLabel = (recipient) => {
    if (recipient.cheque && recipient.cheque.cheque_date) {
      return 'تاريخ الشيك';
    }
    return 'تاريخ الاستحقاق';
  };

  // Apply filters whenever recipients or filter values change
  useEffect(() => {
    let filtered = recipients.filter(r => !r.received);

    // Apply name filter
    if (nameFilter.trim()) {
      filtered = filtered.filter(r => 
        r.student_name && r.student_name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    // Apply date range filter
    if (dateFromFilter || dateToFilter) {
      filtered = filtered.filter(r => {
        const displayDate = dayjs(getDisplayDate(r));
        
        let passesFromFilter = true;
        let passesToFilter = true;
        
        if (dateFromFilter) {
          passesFromFilter = displayDate.isSameOrAfter(dateFromFilter, 'day');
        }
        
        if (dateToFilter) {
          passesToFilter = displayDate.isSameOrBefore(dateToFilter, 'day');
        }
        
        return passesFromFilter && passesToFilter;
      });
    }

    setFilteredRecipients(filtered);
  }, [recipients, nameFilter, dateFromFilter, dateToFilter]);

  // Update table rows when filtered recipients change
  useEffect(() => {
    let data = filteredRecipients;

    // Use display date for grouping (cheque date if exists, otherwise payment date)
    const today = dayjs().startOf('day');
    const todayStr = today.format('YYYY-MM-DD');
    const datesWithPayments = Array.from(
      new Set(
        data.map(r => dayjs(getDisplayDate(r)).format('YYYY-MM-DD'))
      )
    );
    
    // Add today's date if not present and no date filters are applied
    if (!datesWithPayments.includes(todayStr) && !dateFromFilter && !dateToFilter) {
      datesWithPayments.push(todayStr);
    }
    datesWithPayments.sort();

    // Group payments by display date
    const rows = datesWithPayments.map(dateStr => {
      const isToday = dateStr === todayStr;
      const payments = data.filter(
        r => dayjs(getDisplayDate(r)).format('YYYY-MM-DD') === dateStr
      );
      let status = '';
      if (isToday) {
        status = payments.length > 0 ? 'today-has' : 'today-empty';
      } else if (dayjs(dateStr).isAfter(today)) {
        status = 'upcoming';
      } else if (dayjs(dateStr).isBefore(today)) {
        status = 'overdue';
      }
      return {
        date: dateStr,
        isToday,
        payments,
        status,
      };
    });

    setTableRows(rows);
    setAllDates(datesWithPayments);
  }, [filteredRecipients, dateFromFilter, dateToFilter]);

  const handleExpandClick = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };

  // Handle collection confirmation dialog
  const handleCollectionClick = (recipientId, studentName) => {
    setConfirmDialog({
      open: true,
      recipientId,
      studentName
    });
  };

const handleConfirmCollection = async () => {
  const { recipientId } = confirmDialog;
  
  try {
    // Find the recipient data from the current recipients list
    const recipient = recipients.find(r => r.id === recipientId);
    
    if (!recipient) {
      showToast('لم يتم العثور على بيانات الدفعة', 'error');
      return;
    }

    // Prepare the complete payload with all required fields
    const payload = {
      id: recipientId,
      student_id: recipient.student || recipient.student_id,
      amount: Number(recipient.amount),
      date: recipient.date,
      payment_type: recipient.payment_type,
      received: true, // Mark as collected
      // Include other existing fields if they exist
      ...(recipient.school_fee && { school_fee: recipient.school_fee }),
      ...(recipient.note && { note: recipient.note }),
      // Include cheque data if it exists
      ...(recipient.cheque && {
        bankNumber: recipient.cheque.bank_number,
        branchNumber: recipient.cheque.branch_number,
        bankName: recipient.cheque.bank_name,
        branchName: recipient.cheque.branch_name,
        accountNumber: recipient.cheque.account_number,
        chequeNumber: recipient.cheque.cheque_number,
        chequeDueDate: recipient.cheque.cheque_date,
        description: recipient.cheque.description
      }),
      // Include visa data if it exists
      ...(recipient.visa && {
        visaReceiptNumber: recipient.visa.receipt_number,
        visaDescription: recipient.visa.description
      })
    };

    // Update the recipient to mark it as collected
    const updatedRecipient = await dispatch(updateRecipient(payload)).unwrap();

    // Close the dialog
    setConfirmDialog({
      open: false,
      recipientId: null,
      studentName: ''
    });

    // Show success message
    showToast('تم تأكيد التحصيل بنجاح', 'success');

    // Refresh the data by fetching the not received recipients again
    dispatch(fetchNotReceivedRecipients());

  } catch (error) {
    console.error('❌ Failed to mark as collected:', error);
    
    // Close the dialog
    setConfirmDialog({
      open: false,
      recipientId: null,
      studentName: ''
    });
    
    // Show error message
    showToast('فشل في تأكيد التحصيل', 'error');
  }
};

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      recipientId: null,
      studentName: ''
    });
  };

  // Handle delete payment
  const handleDeleteClick = (recipientId, studentName) => {
    setDeleteDialog({ open: true, recipientId, studentName });
  };

  const handleConfirmDelete = async () => {
    const { recipientId } = deleteDialog;
    try {
      await dispatch(deleteRecipient(recipientId)).unwrap();
      setDeleteDialog({ open: false, recipientId: null, studentName: '' });
      showToast('تم حذف الدفعة بنجاح', 'success');
      dispatch(fetchNotReceivedRecipients());
    } catch (error) {
      setDeleteDialog({ open: false, recipientId: null, studentName: '' });
      showToast('فشل في حذف الدفعة', 'error');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, recipientId: null, studentName: '' });
  };

  // Handle view recipient details - redirect in same tab
  const handleViewRecipient = (recipientId) => {
    navigate(`/recipient/view/${recipientId}`);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setNameFilter('');
    setDateFromFilter(null);
    setDateToFilter(null);
  };

  // Check if any filters are active
  const hasActiveFilters = nameFilter.trim() || dateFromFilter || dateToFilter;

  // Print function with updated data mapping including visa receipt number
  const handlePrint = () => {
    const columns = [
      { header: 'اسم الطالب', key: 'studentName' },
      { header: 'تاريخ الدفع/الشيك', key: 'displayDate' },
      { header: 'المبلغ (شيكل)', key: 'amount' },
      { header: 'نوع الدفعة', key: 'paymentType' },
      { header: 'رقم إيصال الفيزا', key: 'visaReceiptNumber' },
      { header: 'الحالة', key: 'status' },
    ];
    const data = filteredRecipients.map(c => ({
      studentName: c.student_name,
      displayDate: getDisplayDate(c),
      amount: c.amount,
      paymentType: c.payment_type,
      visaReceiptNumber: c.visa && c.visa.receipt_number ? c.visa.receipt_number : '-',
      status: c.received ? 'مستلم' : 'غير مستلم',
    }));
    exportToPrintWindow(data, columns, 'كشف التحصيل');
  };

  // Excel export function with updated data mapping including visa receipt number
  const handleExportExcel = () => {
    const columns = [
      { header: 'اسم الطالب', key: 'studentName' },
      { header: 'تاريخ الدفع/الشيك', key: 'displayDate' },
      { header: 'المبلغ (شيكل)', key: 'amount' },
      { header: 'نوع الدفعة', key: 'paymentType' },
      { header: 'رقم إيصال الفيزا', key: 'visaReceiptNumber' },
      { header: 'الحالة', key: 'status' },
    ];
    const data = filteredRecipients.map(c => ({
      studentName: c.student_name,
      displayDate: getDisplayDate(c),
      amount: c.amount,
      paymentType: c.payment_type,
      visaReceiptNumber: c.visa && c.visa.receipt_number ? c.visa.receipt_number : '-',
      status: c.received ? 'مستلم' : 'غير مستلم',
    }));
    exportToExcel(data, columns, 'cheques');
  };

  return (
    // 🎨 Design System Applied
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          p: { xs: 2, md: 3 },
          bgcolor: '#F8F9FF',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          {/* Header Section */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 2.5,
                  width: 52,
                  height: 52,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptLongIcon sx={{ color: '#fff', fontSize: 30 }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  color="#fff"
                  sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
                >
                  كشف التحصيل
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    mt: 0.25,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  متابعة وتحصيل دفعات الطلاب المستحقة والشيكات
                </Typography>
              </Box>
            </Stack>

            <Chip
              label={`${filteredRecipients.length} دفعة`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                px: 1,
                height: 34,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            />
          </MotionPaper>

          {/* Filters Section */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
              <FilterListIcon sx={{ mr: 1, color: '#4338CA' }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                الفلاتر
              </Typography>
              {hasActiveFilters && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{
                    ml: 'auto',
                    color: '#DC2626',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&:hover': { bgcolor: '#FEE2E2' },
                  }}
                >
                  مسح الفلاتر
                </Button>
              )}
            </Box>

            <Grid container spacing={3} sx={{ alignItems: 'flex-end' }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="البحث بالاسم"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="أدخل اسم الطالب"
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      height: '56px',
                      fontSize: '16px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '16px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="من تاريخ"
                  value={dateFromFilter}
                  onChange={(newValue) => setDateFromFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': { 
                          height: '56px',
                          fontSize: '16px'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '16px'
                        },
                        '& .MuiPickersInputBase-root': {
                          flexDirection: 'row-reverse'
                        },
                        '& .css-1hgcujo-MuiPickersInputBase-root-MuiPickersOutlinedInput-root': {
                          flexDirection: 'row-reverse'
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="إلى تاريخ"
                  value={dateToFilter}
                  onChange={(newValue) => setDateToFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': { 
                          height: '56px',
                          fontSize: '16px'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '16px'
                        },
                        '& .MuiPickersInputBase-root': {
                          flexDirection: 'row-reverse'
                        },
                        '& .css-1hgcujo-MuiPickersInputBase-root-MuiPickersOutlinedInput-root': {
                          flexDirection: 'row-reverse'
                        }
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
            
            {/* Filter Results Summary */}
            {hasActiveFilters && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#EEF2FF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label="تم تطبيق الفلاتر"
                  size="small"
                  icon={<FilterListIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: '#4338CA',
                    color: '#fff',
                    fontWeight: 700,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: '#4338CA', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                >
                  إجمالي النتائج: {filteredRecipients.length} دفعة
                </Typography>
              </Box>
            )}
          </MotionPaper>

          {/* Export and Print buttons */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
            sx={{
              mb: 3,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              تصدير القائمة
            </Typography>
            <Stack useFlexGap
              direction="row"
              spacing={1.5}
              sx={{ flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, alignItems: 'center', gap: 1.5 }}
            >
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.5,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#16A34A',
                  color: '#16A34A',
                  '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
                }}
              >
                تصدير Excel
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handlePrint}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.5,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                }}
              >
                طباعة
              </Button>
            </Stack>
          </MotionPaper>

          {/* Results Table */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              mb: 3,
            }}
          >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>
                    التاريخ
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>
                    اسم الطالب
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>
                    تفاصيل الدفعة
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>
                    الإجراءات
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                      <ReceiptLongIcon sx={{ fontSize: 48, color: '#C7D2FE', mb: 1 }} />
                      <Typography variant="subtitle1" sx={{ color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        لا توجد نتائج تطابق الفلاتر المحددة
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRows.map((row) =>
                    row.payments.length === 0 ? null : row.payments.map((p, i) => {
                      // Student data
                      const student = p.student || {};
                      const parentName = p.parent_name || student.parent_name || '---';
                      const parentPhone = p.parent_phone || student.parent_phone || '---';
                      const grade = p.class_name || '---';
                      const fullName = p.student_name;
                      const rowKey = `${row.date}-${p.id || i}`;
                      const displayDate = getDisplayDate(p);
                      const dateTypeLabel = getDateTypeLabel(p);
                      
                      return (
                        <React.Fragment key={rowKey}>
                          <TableRow
                            sx={{
                              background:
                                row.isToday
                                  ? row.status === 'today-has'
                                    ? '#FEF3C7'
                                    : '#E0F2FE'
                                  : row.status === 'upcoming'
                                    ? '#DCFCE7'
                                    : row.status === 'overdue'
                                      ? '#FEE2E2'
                                      : '#fff',
                              borderRight: row.isToday ? '4px solid #4338CA' : '4px solid transparent',
                              transition: 'background 0.2s',
                              '&:hover': { filter: 'brightness(0.985)' },
                            }}
                          >
                            <TableCell align="center" sx={{
                              fontWeight: row.isToday ? 700 : 500,
                              color: row.isToday ? '#4338CA' : row.status === 'overdue' ? '#DC2626' : row.status === 'upcoming' ? '#16A34A' : '#1E1B4B',
                              fontSize: '0.9rem',
                              fontFamily: 'Cairo, Tajawal, sans-serif',
                              borderBottom: '1px solid #E5E7EB',
                            }}>
                              {row.isToday ? (
                                <span>
                                  اليوم <br />
                                  <span style={{ fontSize: 12, color: '#6B7280' }}>{row.date}</span>
                                </span>
                              ) : (
                                <span>{row.date}</span>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>
                              {fullName}
                              {/* Show cheque indicator if it's a cheque payment */}
                              {p.cheque && (
                                <Chip
                                  label="شيك"
                                  size="small"
                                  sx={{ ml: 1, fontSize: '0.7rem', bgcolor: '#E0F2FE', color: '#0284C7', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                                />
                              )}
                              {/* Show visa indicator with receipt number if it's a visa payment */}
                              {p.visa && (
                                <Chip
                                  label={`فيزا - ${p.visa.receipt_number || 'غير محدد'}`}
                                  size="small"
                                  sx={{ ml: 1, fontSize: '0.7rem', bgcolor: '#EEF2FF', color: '#5B21F4', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid #E5E7EB' }}>
                              <Chip
                                label={row.status === 'overdue' ? 'متأخرة' : row.status === 'upcoming' ? 'قادمة' : 'اليوم'}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  fontFamily: 'Cairo, Tajawal, sans-serif',
                                  bgcolor: row.status === 'overdue' ? '#FEE2E2' : row.status === 'upcoming' ? '#DCFCE7' : '#E0F2FE',
                                  color: row.status === 'overdue' ? '#DC2626' : row.status === 'upcoming' ? '#16A34A' : '#0284C7',
                                }}
                              />
                              <Typography sx={{ mt: 1, fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                {p.amount} شيكل - {p.payment_type || '-'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                {dateTypeLabel}: {dayjs(displayDate).format('YYYY-MM-DD')}
                              </Typography>
                              {/* Show visa receipt number if available */}
                              {p.visa && p.visa.receipt_number && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#5B21F4', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                  رقم الإيصال: {p.visa.receipt_number}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid #E5E7EB' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, alignItems: 'center' }}>
                                {/* Collection Button */}
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<TaskAltIcon />}
                                  onClick={() => handleCollectionClick(p.id, p.student_name)}
                                  sx={{
                                    borderRadius: 2.5,
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.6,
                                    minWidth: 150,
                                    bgcolor: '#16A34A',
                                    boxShadow: 'none',
                                    fontFamily: 'Cairo, Tajawal, sans-serif',
                                    '&:hover': { bgcolor: '#15803D', boxShadow: 'none' },
                                  }}
                                >
                                  تغيير الى تم التحصيل
                                </Button>
                                {/* WhatsApp Button for overdue payments */}
                                {row.status === 'overdue' && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<WhatsAppIcon sx={{ mr: 1.2 }} />}
                                    sx={{
                                      borderRadius: 2.5,
                                      fontWeight: 700,
                                      fontSize: '0.78rem',
                                      textTransform: 'none',
                                      px: 2,
                                      py: 0.6,
                                      minWidth: 150,
                                      boxShadow: 'none',
                                      fontFamily: 'Cairo, Tajawal, sans-serif',
                                      backgroundColor: '#25D366',
                                      '&:hover': { backgroundColor: '#1ebe5d', boxShadow: 'none' },
                                    }}
                                    onClick={() => {
                                      const formattedPhone = parentPhone;
                                      const buildMessage = (r) => (
                                        `تنبيه بخصوص دفعة متأخرة:\n` +
                                        `اسم الطالب: ${r.student_name || '-'}\n` +
                                        `اسم ولي الأمر: ${r.parent_name || '-'}\n` +
                                        `المبلغ: ${r.amount || '-'} شيكل\n` +
                                        `نوع الدفعة: ${r.payment_type || '-'}\n` +
                                        `تاريخ الاستحقاق: ${getDisplayDate(r) || '-'}\n` +
                                        `منشئ السند: ${r.created_by_name || '-'}\n` +
                                        (r.cheque ?
                                          `--- تفاصيل الشيك ---\n` +
                                          `رقم الشيك: ${r.cheque.cheque_number || '-'}\n` +
                                          `تاريخ الشيك: ${r.cheque.cheque_date || '-'}\n` +
                                          `البنك: ${bankLabel(r.cheque) || '-'}\n` +
                                          `الفرع: ${branchLabel(r.cheque) || '-'}\n` +
                                          `رقم الحساب: ${r.cheque.account_number || '-'}\n`
                                          : ''
                                        ) +
                                        (r.visa ?
                                          `--- تفاصيل الفيزا ---\n` +
                                          `رقم الإيصال: ${r.visa.receipt_number || '-'}\n` +
                                          `الوصف: ${r.visa.description || '-'}\n`
                                          : ''
                                        )
                                      );
                                      if (!formattedPhone) {
                                        alert('لا يوجد رقم هاتف صحيح لولي الأمر');
                                        return;
                                      }
                                      const url = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(buildMessage(p))}`;
                                      window.open(url, '_blank');
                                    }}
                                  >
                                    تذكير واتساب
                                  </Button>
                                )}
                                
                                {/* View Details + Expand actions */}
                                <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                                  <Tooltip title="عرض التفاصيل">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewRecipient(p.id)}
                                      sx={{
                                        color: '#4338CA',
                                        '&:hover': { bgcolor: '#EEF2FF' },
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={expandedRows[rowKey] ? 'إخفاء التفاصيل' : 'عرض المزيد'}>
                                    <IconButton
                                      onClick={() => handleExpandClick(rowKey)}
                                      size="small"
                                      sx={{
                                        color: '#4338CA',
                                        transition: 'transform 0.2s',
                                        transform: expandedRows[rowKey] ? 'rotate(180deg)' : 'rotate(0deg)',
                                        '&:hover': { bgcolor: '#EEF2FF' },
                                      }}
                                    >
                                      {expandedRows[rowKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ p: 0, border: 0, background: 'transparent' }}>
                              <Collapse in={!!expandedRows[rowKey]} timeout="auto" unmountOnExit>
                                <Box sx={{
                                  p: 3,
                                  bgcolor: '#F8F9FF',
                                  borderRadius: 3,
                                  border: '1px solid #E5E7EB',
                                  my: 1,
                                  mx: 2,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 3,
                                  justifyContent: 'center'
                                }}>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>اسم الطالب</Typography>
                                    <Typography>{fullName}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الهوية</Typography>
                                    <Typography>{p.student_id || student.student_id || '-'}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>اسم ولي الأمر</Typography>
                                    <Typography>{parentName}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم هاتف ولي الأمر</Typography>
                                    <Typography>{parentPhone}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الصف</Typography>
                                    <Typography>{grade}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>قيمة الدفعة</Typography>
                                    <Typography>{p.amount} شيكل</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>نوع الدفعة</Typography>
                                    <Typography>{p.payment_type || '-'}</Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 150 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{dateTypeLabel}</Typography>
                                    <Typography>{dayjs(displayDate).format('YYYY-MM-DD')}</Typography>
                                  </Box>
                                  {/* NEW: Creator Name */}
                                  <Box sx={{ minWidth: 150 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>منشئ السند</Typography>
                                    <Typography>{p.created_by_name || '-'}</Typography>
                                  </Box>
                                  {/* Show original payment date if cheque date is different */}
                                  {p.cheque && p.cheque.cheque_date !== p.date && (
                                    <Box sx={{ minWidth: 150 }}>
                                      <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>تاريخ الاستحقاق الأصلي</Typography>
                                      <Typography>{dayjs(p.date).format('YYYY-MM-DD')}</Typography>
                                    </Box>
                                  )}
                                  {/* Show cheque details if available */}
                                  {p.cheque && (
                                    <>
                                      <Box sx={{ minWidth: 120 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>البنك</Typography>
                                        <Typography>{bankLabel(p.cheque) || '-'}</Typography>
                                      </Box>
                                      <Box sx={{ minWidth: 120 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الفرع</Typography>
                                        <Typography>{branchLabel(p.cheque) || '-'}</Typography>
                                      </Box>
                                      <Box sx={{ minWidth: 150 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الحساب</Typography>
                                        <Typography>{p.cheque.account_number || '-'}</Typography>
                                      </Box>
                                      {p.cheque.cheque_number && (
                                        <Box sx={{ minWidth: 120 }}>
                                          <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الشيك</Typography>
                                          <Typography>{p.cheque.cheque_number}</Typography>
                                        </Box>
                                      )}
                                      {p.cheque.cheque_image && (
                                        <Box sx={{ minWidth: 200 }}>
                                          <Typography sx={{ fontWeight: 700, color: '#4338CA', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>صورة الشيك</Typography>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => window.open(p.cheque.cheque_image, '_blank')}
                                            sx={{
                                              mt: 1,
                                              borderRadius: 2.5,
                                              textTransform: 'none',
                                              fontWeight: 700,
                                              fontFamily: 'Cairo, Tajawal, sans-serif',
                                              borderColor: '#4338CA',
                                              color: '#4338CA',
                                              '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                                            }}
                                          >
                                            عرض الصورة
                                          </Button>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                  {/* Show visa details if available */}
                                  {p.visa && (
                                    <>
                                      <Box sx={{ minWidth: 150 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#5B21F4', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم إيصال الفيزا</Typography>
                                        <Typography>{p.visa.receipt_number || '-'}</Typography>
                                      </Box>
                                      {p.visa.description && (
                                        <Box sx={{ minWidth: 200 }}>
                                          <Typography sx={{ fontWeight: 700, color: '#5B21F4', fontSize: '0.78rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>وصف الفيزا</Typography>
                                          <Typography>{p.visa.description}</Typography>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                  {/* Delete Button */}
                                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      startIcon={<DeleteIcon />}
                                      onClick={() => handleDeleteClick(p.id, p.student_name)}
                                      sx={{
                                        borderRadius: 2.5,
                                        fontWeight: 700,
                                        fontSize: '0.78rem',
                                        textTransform: 'none',
                                        px: 2,
                                        py: 0.5,
                                        fontFamily: 'Cairo, Tajawal, sans-serif',
                                        borderColor: '#DC2626',
                                        color: '#DC2626',
                                        '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
                                      }}
                                    >
                                      حذف الدفعة
                                    </Button>
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </MotionPaper>
        </Box>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: 400,
              textAlign: 'center'
            }
          }}
        >
          <DialogTitle
            id="confirm-dialog-title"
            sx={{
              fontWeight: 800,
              color: '#1E1B4B',
              fontSize: 20,
              pb: 1,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#16A34A' }} />
            تأكيد التحصيل
          </DialogTitle>
          <DialogContent>
            <DialogContentText 
              id="confirm-dialog-description"
              sx={{ 
                fontSize: 16, 
                color: 'text.primary',
                textAlign: 'center',
                py: 2
              }}
            >
              هل أنت متأكد من أنه تم تحصيل الدفعة من الطالب:{' '}
              <strong style={{ color: '#1976d2' }}>{confirmDialog.studentName}</strong>؟
              <br />
              <span style={{ fontSize: 14, color: '#666', marginTop: 8, display: 'block' }}>
                لا يمكن التراجع عن هذا الإجراء
              </span>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
            <Button
              onClick={handleCloseConfirmDialog}
              variant="outlined"
              color="inherit"
              sx={{
                borderRadius: 2.5,
                px: 3,
                fontWeight: 700,
                textTransform: 'none',
                minWidth: 100,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleConfirmCollection}
              variant="contained"
              autoFocus
              sx={{
                borderRadius: 2.5,
                px: 3,
                fontWeight: 700,
                textTransform: 'none',
                minWidth: 100,
                bgcolor: '#16A34A',
                boxShadow: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': { bgcolor: '#15803D', boxShadow: 'none' },
              }}
            >
              تأكيد التحصيل
            </Button>
          </DialogActions>
        </Dialog>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: 400,
              textAlign: 'center'
            }
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              color: '#DC2626',
              fontSize: 20,
              pb: 1,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#DC2626' }} />
            تأكيد حذف الدفعة
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={{
                fontSize: 16,
                color: 'text.primary',
                textAlign: 'center',
                py: 2
              }}
            >
              هل أنت متأكد من حذف دفعة الطالب:{' '}
              <strong style={{ color: '#d32f2f' }}>{deleteDialog.studentName}</strong>؟
              <br />
              <span style={{ fontSize: 14, color: '#666', marginTop: 8, display: 'block' }}>
                لا يمكن التراجع عن هذا الإجراء
              </span>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: 'none', minWidth: 100, fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              autoFocus
              sx={{
                borderRadius: 2.5,
                px: 3,
                fontWeight: 700,
                textTransform: 'none',
                minWidth: 100,
                bgcolor: '#DC2626',
                boxShadow: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
              }}
            >
              حذف
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ChequesPage;